import type { StaticEntityType } from "@config";
import { useIsEditorContext } from "@editor-providers";
import { cn } from "@lib/cn";
import {
  lazy,
  memo,
  Suspense,
  useCallback,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ComponentType,
} from "react";
import { TextLibrary } from "../../text/components/Text.library";
import { TABLE_STICKY_TOP_OFFSET } from "../constants";
import {
  useTableCellStyles,
  type ITableCellStyleSettings,
} from "../hooks/use-table-cell-styles";
import {
  TableHeaderPosition,
  type IFormulaEditState,
  type ITableCell,
  type ITableCellDropTargetProps,
  type ITableCellFroalaProps,
  type ITableCellObserversProps,
  type ITableCellStackProps,
  type ITableSpecificSettings,
} from "../types";
import { isTableCellHeader } from "../utils/is-table-cell-header";
import { extractCellText } from "../utils/extract-cell-text";
import { isValidInCellDropTarget } from "../utils/is-valid-in-cell-drop-target";
import { renderCalcResult } from "../utils/render-calc-formatting";
import {
  computeCalcCellHighlightStyles,
  computeCalcCellTdStyles,
} from "../utils/calc-cell-styles";
import { FormulaEditInput } from "./formula-edit-input";

// Lazy to break the circular init between the table and the generic element
// registry (Elements includes Table itself).
const LazyElementLibrary = lazy(() =>
  import("../../../components/Element.library").then((m) => ({
    default: m.ElementLibrary,
  })),
);

// Primitive flags derived from editorData, computed once per render in
// TableRows and passed down. Keeping these as primitives lets TableCell's
// memo bail out for unaffected cells on a selection change — only the old
// and new active/selected cells actually re-render.
export interface ITableCellEditorFlags {
  isActive: boolean;
  isRovingFocus: boolean;
  isInSelection: boolean;
  isTableActive: boolean;
  // True when this cell hosts in-cell element children (tables 2.0); gates
  // the Froala suppression for stack-only cells.
  hasInCellChildren: boolean;
  // True iff some cell in the table is the roving-focus target. When false,
  // cell (0,0) acts as the tab-anchor so the table is keyboard-reachable.
  hasRovingFocus: boolean;
  navigationMode: boolean;
  // Edge flags are computed on EVERY cell (selected or not): true when the
  // selection state differs between this cell and the neighbor on that side,
  // i.e. the edge is on the selection perimeter and should be colored primary.
  // This lets unselected cells bordering a selected block promote their own
  // bottom/right borders (which are the edges they physically draw under the
  // table's "interior top/left are 0" convention).
  crossingTop: boolean;
  crossingBottom: boolean;
  crossingLeft: boolean;
  crossingRight: boolean;
  selectionOverlayActive: boolean;
  FroalaEditor: ComponentType<ITableCellFroalaProps>;
  TableCellEditorObserver: ComponentType<ITableCellObserversProps>;
  // Formula-edit actions injected from @editor (keeps @library store-free).
  startFormulaEdit: (payload: IFormulaEditState) => void;
  updateFormulaEdit: (buffer: string) => void;
  stopFormulaEdit: () => void;
}

interface IProps {
  cell: ITableCell;
  idx: number;
  row: number;
  visualRow: number;
  col: number;
  entityId: string;
  settings: ITableCellStyleSettings &
    Pick<ITableSpecificSettings, "tableCellVerticalAlignment">;
  editor?: ITableCellEditorFlags;
  frozenStickyTop?: number;
  isFrozenBandEdge?: boolean;
  // True when the neighbour above/left actually draws the shared edge
  // (single-stroke border suppression; see TableRows for the full rationale).
  topNeighborDrawsBottom: boolean;
  leftNeighborDrawsRight: boolean;
  // Evaluated formula result (formatted) for calc cells; undefined for plain cells.
  calcDisplay?: string;
  // Set only on the cell currently being formula-edited (carries the live buffer).
  formulaEditBuffer?: string;
  // True when this cell is referenced by the formula being edited.
  isFormulaReferenced?: boolean;
  // Formula-reference (blue) perimeter edges.
  formulaCrossingTop?: boolean;
  formulaCrossingBottom?: boolean;
  formulaCrossingLeft?: boolean;
  formulaCrossingRight?: boolean;
  // Calc/edit-target (green) perimeter edges.
  formulaCrossingGreenTop?: boolean;
  formulaCrossingGreenBottom?: boolean;
  formulaCrossingGreenLeft?: boolean;
  formulaCrossingGreenRight?: boolean;
  // Editor-injected resolver for `{{var}}` tokens in the read-only render (version mode).
  resolveCellContent?: (content: string) => string;
  // Static render path: the cell's in-cell element children (nested published
  // JSON, already filtered by allow-list). Editor contexts use InCellStack.
  staticChildren?: StaticEntityType[];
  // Live stack presence, sourced outside editorData (undefined on an inactive
  // table) so the gate below survives the table losing focus.
  hasStackChildren?: boolean;
  // Editor-injected live stack renderer; present in editor contexts even when
  // the table is inactive (unlike `editor`, which is active-only).
  InCellStack?: ComponentType<ITableCellStackProps>;
  // Editor-injected slide→cell drop target (dnd-kit lives in @editor).
  CellDropTarget?: ComponentType<ITableCellDropTargetProps>;
}

const areTableCellPropsEqual = (prev: IProps, next: IProps): boolean => {
  if (
    prev.idx !== next.idx ||
    prev.row !== next.row ||
    prev.visualRow !== next.visualRow ||
    prev.col !== next.col ||
    prev.frozenStickyTop !== next.frozenStickyTop ||
    prev.isFrozenBandEdge !== next.isFrozenBandEdge ||
    prev.topNeighborDrawsBottom !== next.topNeighborDrawsBottom ||
    prev.leftNeighborDrawsRight !== next.leftNeighborDrawsRight ||
    prev.calcDisplay !== next.calcDisplay ||
    prev.formulaEditBuffer !== next.formulaEditBuffer ||
    prev.isFormulaReferenced !== next.isFormulaReferenced ||
    prev.formulaCrossingTop !== next.formulaCrossingTop ||
    prev.formulaCrossingBottom !== next.formulaCrossingBottom ||
    prev.formulaCrossingLeft !== next.formulaCrossingLeft ||
    prev.formulaCrossingRight !== next.formulaCrossingRight ||
    prev.formulaCrossingGreenTop !== next.formulaCrossingGreenTop ||
    prev.formulaCrossingGreenBottom !== next.formulaCrossingGreenBottom ||
    prev.formulaCrossingGreenLeft !== next.formulaCrossingGreenLeft ||
    prev.formulaCrossingGreenRight !== next.formulaCrossingGreenRight
  )
    return false;

  // Reference equality is enough: TableRows memoizes settings so the reference
  // only changes when a relevant field actually changed.
  if (prev.settings !== next.settings) return false;
  if (prev.entityId !== next.entityId) return false;
  // Static children come from the entity's nested JSON; identity changes only
  // when the published content does. InCellStack / CellDropTarget are module-stable.
  if (prev.staticChildren !== next.staticChildren) return false;
  if (prev.hasStackChildren !== next.hasStackChildren) return false;
  if (prev.InCellStack !== next.InCellStack) return false;
  if (prev.CellDropTarget !== next.CellDropTarget) return false;
  // Identity changes only when version data changes (memoized in TableEditor),
  // so inactive cells re-resolve their pills when the variable values update.
  if (prev.resolveCellContent !== next.resolveCellContent) return false;

  const prevE = prev.editor;
  const nextE = next.editor;
  if (!prevE !== !nextE) return false;
  if (prevE && nextE) {
    if (
      prevE.isActive !== nextE.isActive ||
      prevE.isRovingFocus !== nextE.isRovingFocus ||
      prevE.isInSelection !== nextE.isInSelection ||
      prevE.isTableActive !== nextE.isTableActive ||
      prevE.hasInCellChildren !== nextE.hasInCellChildren ||
      prevE.hasRovingFocus !== nextE.hasRovingFocus ||
      prevE.navigationMode !== nextE.navigationMode ||
      prevE.crossingTop !== nextE.crossingTop ||
      prevE.crossingBottom !== nextE.crossingBottom ||
      prevE.crossingLeft !== nextE.crossingLeft ||
      prevE.crossingRight !== nextE.crossingRight ||
      prevE.selectionOverlayActive !== nextE.selectionOverlayActive ||
      prevE.FroalaEditor !== nextE.FroalaEditor ||
      prevE.TableCellEditorObserver !== nextE.TableCellEditorObserver
    ) {
      return false;
    }
  }

  const a = prev.cell;
  const b = next.cell;

  return (
    a.id === b.id &&
    a.content === b.content &&
    a.bgColor === b.bgColor &&
    a.verticalAlignment === b.verticalAlignment &&
    a.horizontalAlignment === b.horizontalAlignment &&
    a.textRotation === b.textRotation &&
    a.colSpan === b.colSpan &&
    a.rowSpan === b.rowSpan &&
    a.hasFroalaFontSize === b.hasFroalaFontSize &&
    a.borderTopColor === b.borderTopColor &&
    a.borderTopWidth === b.borderTopWidth &&
    a.borderTopStyle === b.borderTopStyle &&
    a.borderBottomColor === b.borderBottomColor &&
    a.borderBottomWidth === b.borderBottomWidth &&
    a.borderBottomStyle === b.borderBottomStyle &&
    a.borderLeftColor === b.borderLeftColor &&
    a.borderLeftWidth === b.borderLeftWidth &&
    a.borderLeftStyle === b.borderLeftStyle &&
    a.borderRightColor === b.borderRightColor &&
    a.borderRightWidth === b.borderRightWidth &&
    a.borderRightStyle === b.borderRightStyle &&
    a.borderColor === b.borderColor &&
    a.borderWidth === b.borderWidth &&
    a.borderStyle === b.borderStyle &&
    a.mergedInto === b.mergedInto &&
    a.calcFormatHtml === b.calcFormatHtml &&
    a.calculation?.formula === b.calculation?.formula
  );
};

export const TableCell = memo(
  ({
    col,
    idx,
    row,
    visualRow,
    cell,
    entityId,
    settings,
    editor,
    frozenStickyTop,
    isFrozenBandEdge,
    topNeighborDrawsBottom,
    leftNeighborDrawsRight,
    calcDisplay,
    formulaEditBuffer,
    isFormulaReferenced,
    formulaCrossingTop,
    formulaCrossingBottom,
    formulaCrossingLeft,
    formulaCrossingRight,
    formulaCrossingGreenTop,
    formulaCrossingGreenBottom,
    formulaCrossingGreenLeft,
    formulaCrossingGreenRight,
    resolveCellContent,
    staticChildren,
    hasStackChildren: hasStackChildrenProp,
    InCellStack,
    CellDropTarget,
  }: IProps) => {
    const {
      tableCellVerticalAlignment,
      tableHasHeader,
      tableHeaderPosition,
      tableRows,
      tableCols,
    } = settings;

    const isEditor = useIsEditorContext();
    const cellRef = useRef<HTMLTableDataCellElement | null>(null);

    // In version mode the editor supplies a resolver that turns `{{var}}`
    // tokens into resolved pills for the inactive (read-only) render — mirroring
    // the rich-text element. Without it (viewer / non-version) the raw content is
    // used unchanged. The active Froala editor never sees this; it keeps raw
    // templates so the save path is unaffected.
    const displayContent = useMemo(
      () =>
        resolveCellContent ? resolveCellContent(cell.content) : cell.content,
      [resolveCellContent, cell.content],
    );

    const isHeader = isTableCellHeader({
      cellIdx: idx,
      tableHasHeader,
      tableHeaderPosition,
      tableCols,
      tableRows,
    });

    const isColumnHeader =
      isHeader &&
      (tableHeaderPosition === TableHeaderPosition.Top ||
        tableHeaderPosition === TableHeaderPosition.Bottom);

    const { tdStyles, bgStyles, contentRotationStyle, borderOverlay } =
      useTableCellStyles({
        cell,
        col,
        row: visualRow,
        isHeader,
        topNeighborDrawsBottom,
        leftNeighborDrawsRight,
        ...settings,
      });

    const [droppableSetNodeRef, setDroppableSetNodeRef] = useState<
      ((node: HTMLElement | null) => void) | null
    >(null);

    const registerSetNodeRef = useCallback(
      (setNodeRef: (node: HTMLElement | null) => void) => {
        setDroppableSetNodeRef(() => setNodeRef);
      },
      [],
    );

    const canDropSlideElement = isValidInCellDropTarget(cell);

    const setCellRefs = useCallback(
      (node: HTMLTableDataCellElement | null) => {
        cellRef.current = node;
        droppableSetNodeRef?.(node);
      },
      [droppableSetNodeRef],
    );

    // A merged-into cell renders nothing. This early-return MUST come after
    // every Hook above — otherwise hooks would be skipped for merged cells,
    // changing Hook order between renders and crashing React (Rules of Hooks).
    if (cell.mergedInto) return null;

    const stickyStyle: CSSProperties = {};
    if (isHeader && tableHasHeader) {
      stickyStyle.position = "sticky";
      if (
        tableHeaderPosition === TableHeaderPosition.Top ||
        tableHeaderPosition === TableHeaderPosition.Bottom
      ) {
        if (tableHeaderPosition === TableHeaderPosition.Top) {
          // Park below the fixed Document header instead of behind it; the
          // variable is 0px wherever that bar takes no space (see TableView).
          stickyStyle.top = TABLE_STICKY_TOP_OFFSET;
        } else {
          stickyStyle.bottom = 0;
        }
        stickyStyle.zIndex = col === 0 ? 42 : 40;
      } else {
        stickyStyle[
          tableHeaderPosition === TableHeaderPosition.Left ? "left" : "right"
        ] = 0;
        stickyStyle.zIndex = row === 0 ? 42 : 40;
      }
    }

    if (frozenStickyTop !== undefined && !isEditor) {
      stickyStyle.position = "sticky";
      delete stickyStyle.bottom;
      stickyStyle.top = `calc(${TABLE_STICKY_TOP_OFFSET} + ${frozenStickyTop}px)`;
      const frozenZ = col === 0 ? 33 : 32;
      stickyStyle.zIndex =
        typeof stickyStyle.zIndex === "number"
          ? Math.max(stickyStyle.zIndex, frozenZ)
          : frozenZ;
      stickyStyle.backgroundClip = "border-box";
      const ownBg = tdStyles.background;
      if (
        !ownBg ||
        ownBg === "transparent" ||
        ownBg === "none" ||
        ownBg === "rgba(0, 0, 0, 0)"
      ) {
        stickyStyle.backgroundColor =
          "var(--table-cell-bg, var(--background-main-color, #fff))";
      }
    }

    const ariaRole = isHeader
      ? tableHeaderPosition === TableHeaderPosition.Left ||
        tableHeaderPosition === TableHeaderPosition.Right
        ? "rowheader"
        : "columnheader"
      : "gridcell";

    // Roving tabindex: the cell that should receive tab focus first is
    // either (a) the current roving-focus cell, or (b) cell (0,0) as the
    // anchor when no cell has roving focus yet. Without (b), tabbing into
    // an active table whose selection has been cleared lands nowhere and
    // the table becomes keyboard-unreachable.
    const tabIndex = editor
      ? editor.isRovingFocus
        ? 0
        : !editor.hasRovingFocus && row === 0 && col === 0
          ? 0
          : -1
      : undefined;

    const ariaSelected = editor
      ? editor.isInSelection || editor.isActive
      : undefined;

    // A cell hosting an element stack with no text of its own must NOT mount
    // Froala on activation: the empty editor line would push the stack down
    // (layout jump) and re-introduce a second text-entry surface. The floating
    // toolbar is surfaced via the observer's emitToolbarRefresh instead.
    const hasStackChildren =
      !!hasStackChildrenProp ||
      !!editor?.hasInCellChildren ||
      !!staticChildren?.length;
    const isStackOnlyCell =
      hasStackChildren && extractCellText(cell.content ?? "").length === 0;

    const shouldShowFroala =
      editor?.isTableActive &&
      editor.isActive &&
      isEditor &&
      !editor.navigationMode &&
      !isStackOnlyCell;

    const isInSelection =
      !!editor?.isTableActive && isEditor && editor.isInSelection;

    const isCalc = !!cell.calculation && !!calcDisplay;
    const isCalcFocused = isCalc && !!editor?.isActive;
    const isFormulaEditing = formulaEditBuffer !== undefined;

    const calcCellFlags = {
      isInSelection,
      formulaCrossingTop,
      formulaCrossingBottom,
      formulaCrossingLeft,
      formulaCrossingRight,
      formulaCrossingGreenTop,
      formulaCrossingGreenBottom,
      formulaCrossingGreenLeft,
      formulaCrossingGreenRight,
      isCalc,
      isCalcFocused,
      isFormulaEditing,
      isFormulaReferenced,
      selectionOverlayActive: !!editor?.selectionOverlayActive,
    };
    const finalTdStyles = computeCalcCellTdStyles(tdStyles, calcCellFlags);
    const highlightStyles = computeCalcCellHighlightStyles(calcCellFlags);

    const FroalaEditor = editor?.FroalaEditor;
    const TableCellEditorObserver = editor?.TableCellEditorObserver;

    return (
      <td
        ref={setCellRefs}
        key={cell.id}
        role={ariaRole}
        aria-colindex={col + 1}
        aria-selected={ariaSelected}
        tabIndex={tabIndex}
        data-cell-id={cell.id}
        data-cell-row={row}
        data-cell-col={col}
        data-sortable={isColumnHeader || undefined}
        data-frozen-row={
          frozenStickyTop !== undefined && !isEditor ? "" : undefined
        }
        data-frozen-band-edge={isFrozenBandEdge ? "" : undefined}
        data-calculated={isCalc ? "" : undefined}
        data-calc-focused={isCalcFocused ? "" : undefined}
        data-h-align={cell.horizontalAlignment ?? undefined}
        colSpan={cell.colSpan}
        rowSpan={cell.rowSpan}
        data-has-font-size={cell.hasFroalaFontSize}
        style={{ ...finalTdStyles, ...stickyStyle }}
        className="tw-relative tw-outline-none focus:tw-outline-none focus-visible:tw-outline-none"
      >
        {CellDropTarget && (
          <CellDropTarget
            tableId={entityId}
            cellId={cell.id}
            cell={cell}
            canDrop={canDropSlideElement}
            hasStackChildren={hasStackChildren}
            registerSetNodeRef={registerSetNodeRef}
          />
        )}

        <div
          className="tw-pointer-events-none tw-absolute"
          style={{ ...borderOverlay, zIndex: 1 }}
          aria-hidden
        />

        {highlightStyles && (
          // Above the border overlay (zIndex 1): the cell borders straddle the
          // same edges and would otherwise paint over these 1px affordances.
          <div
            className="tw-pointer-events-none tw-absolute tw-inset-0"
            style={{ ...highlightStyles, zIndex: 2 }}
            aria-hidden
          />
        )}

        <div
          className="tw-pointer-events-none tw-absolute tw-left-0 tw-top-0 -tw-z-[1] tw-size-full"
          style={bgStyles}
        />

        {isInSelection && !editor?.selectionOverlayActive && (
          <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-z-0 tw-bg-primary/5" />
        )}

        {isEditor && (isFormulaEditing || isCalc) && (
          <button
            type="button"
            className="tw-absolute tw-right-0 tw-top-0 tw-z-[60] tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-border-0 tw-bg-green-500 tw-text-black"
            style={{
              width: 16,
              height: 16,
              borderBottomLeftRadius: 2,
              padding: 0,
            }}
            aria-label={
              isFormulaEditing ? "Formula edit active" : "Edit formula"
            }
            // Keep the roving-tabindex intact: only the active cell's button is
            // tab-reachable, not every calc cell's.
            tabIndex={editor?.isActive ? 0 : -1}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              if (isFormulaEditing) return;
              const formula = cell.calculation?.formula;
              if (!formula) return;
              editor?.startFormulaEdit({
                entityId,
                cellId: cell.id,
                buffer: formula,
              });
            }}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M18 16v2a1 1 0 0 1 -1 1h-11l6 -7l-6 -7h11a1 1 0 0 1 1 1v2" />
            </svg>
          </button>
        )}

        {editor?.isTableActive && col < tableCols - 1 && (
          <div
            data-is-col-resize
            className="tw-absolute tw-right-[-4px] tw-top-0 tw-z-50 tw-h-full tw-w-[8px] hover:tw-cursor-col-resize"
          />
        )}

        {editor?.isTableActive && row < tableRows - 1 && (
          <div
            data-is-row-resize
            className="tw-absolute tw-bottom-[-4px] tw-left-0 tw-z-50 tw-h-[8px] tw-w-full hover:tw-cursor-row-resize"
          />
        )}

        <div
          className="table-cell-content tw-relative tw-z-30 tw-flex tw-size-full tw-items-start tw-justify-center tw-gap-3 tw-break-all tw-p-3"
          style={
            {
              // Alignment inline so cascade/HMR can't bypass it. Column flow ⇒
              // align-items is the cross (horizontal) axis, justify is vertical.
              flexDirection: "column",
              alignItems: cell.horizontalAlignment ?? "stretch",
              justifyContent:
                cell.verticalAlignment ?? tableCellVerticalAlignment,
              "--cell-v-align":
                cell.verticalAlignment ?? tableCellVerticalAlignment,
              "--cell-h-align": cell.horizontalAlignment ?? "stretch",
            } as CSSProperties
          }
        >
          {/* Hidden (not unmounted) when stack-only: keeps the editor observer
              alive while removing the empty host from the flex gap. */}
          <div
            className={cn(
              !cell.textRotation && "tw-w-full",
              isStackOnlyCell && "tw-hidden",
            )}
            style={contentRotationStyle}
          >
            {formulaEditBuffer !== undefined && editor ? (
              <FormulaEditInput
                entityId={entityId}
                cellId={cell.id}
                buffer={formulaEditBuffer}
                onUpdate={editor.updateFormulaEdit}
                onStop={editor.stopFormulaEdit}
              />
            ) : isCalc && calcDisplay !== undefined ? (
              <TextLibrary
                editorData={{
                  isAdmin: false,
                  content: renderCalcResult(cell.calcFormatHtml, calcDisplay),
                }}
                entity={{
                  settings: {},
                  id: `${entityId}-${cell.id}-calc`,
                  children: [],
                  type: "element",
                }}
              />
            ) : shouldShowFroala && FroalaEditor && TableCellEditorObserver ? (
              <>
                <TableCellEditorObserver cellRef={cellRef} />
                <FroalaEditor cell={cell} />
              </>
            ) : (
              <>
                {/* Active stack-only cell: no Froala, but the floating toolbar
                    still needs its refresh/destroy events (alignment etc.). */}
                {isStackOnlyCell &&
                  editor?.isTableActive &&
                  editor.isActive &&
                  isEditor &&
                  !editor.navigationMode &&
                  TableCellEditorObserver && (
                    <TableCellEditorObserver
                      cellRef={cellRef}
                      emitToolbarRefresh
                    />
                  )}
                <TextLibrary
                  editorData={{
                    isAdmin: false,
                    content: displayContent,
                  }}
                  entity={{
                    settings: {},
                    id: `${entityId}-${cell.id}`,
                    children: [],
                    type: "element",
                  }}
                />
              </>
            )}
          </div>

          {/* In-cell element stack (tables 2.0): live editor children via the
              injected renderer, or the published nested children statically.
              Calc cells are exclusive with children (insert-time guard). */}
          {!isCalc && InCellStack ? (
            <InCellStack cellId={cell.id} />
          ) : !isCalc && staticChildren?.length ? (
            <div className="table-cell-children tw-flex tw-w-full tw-flex-col">
              <Suspense fallback={null}>
                {staticChildren.map((child) => (
                  <LazyElementLibrary key={child.id} entity={child} />
                ))}
              </Suspense>
            </div>
          ) : null}
        </div>
      </td>
    );
  },
  areTableCellPropsEqual,
);
